import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceDot,
  Tooltip,
} from "recharts";

const DATA = [
  { d: 0, e: 118, node: "Source / Well" },
  { d: 0.6, e: 116 },
  { d: 1.3, e: 124 },
  { d: 2.1, e: 133 },
  { d: 2.8, e: 128 },
  { d: 3.6, e: 142, node: "Ridge Crest" },
  { d: 4.4, e: 138 },
  { d: 5.2, e: 149 },
  { d: 6.0, e: 156 },
  { d: 6.8, e: 151 },
  { d: 7.5, e: 162, node: "Field Outlet" },
];

const NODES = DATA.filter((p) => p.node);

const NodeTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-slate-200 bg-white/95 px-3 py-2 shadow-lg backdrop-blur">
      <p className="font-mono text-[11px] uppercase tracking-wider text-[#1a6fc4]">
        {p.node || "Route point"}
      </p>
      <p className="mt-0.5 text-xs text-slate-600">
        Distance {p.d.toFixed(1)} km &middot; Elevation {p.e} m
      </p>
    </div>
  );
};

export const ElevationChart = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  return (
    <div
      ref={ref}
      data-testid="elevation-chart"
      className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#1a6fc4]">
            Elevation Profile
          </p>
          <p className="mt-1 font-heading text-lg font-bold tracking-tight text-slate-900">
            Ground level along the pipeline route
          </p>
        </div>
        <div className="flex items-center gap-4 font-mono text-[11px] text-slate-500">
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#1a6fc4]" /> Elevation
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#5aa72f]" /> Node
          </span>
        </div>
      </div>

      <div className="h-[280px] w-full sm:h-[320px]">
        {inView && (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={DATA} margin={{ top: 10, right: 10, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="pmFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1a6fc4" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#1a6fc4" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <XAxis
                dataKey="d"
                tickFormatter={(v) => `${v}km`}
                tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "JetBrains Mono" }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[100, 175]}
                tick={{ fontSize: 11, fill: "#94a3b8", fontFamily: "JetBrains Mono" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${v}m`}
              />
              <Tooltip content={<NodeTip />} cursor={{ stroke: "#cbd5e1", strokeDasharray: "4 4" }} />
              <Area
                type="monotone"
                dataKey="e"
                stroke="#1a6fc4"
                strokeWidth={2.5}
                fill="url(#pmFill)"
                isAnimationActive
                animationDuration={1800}
                animationEasing="ease-out"
                dot={false}
                activeDot={{ r: 5, fill: "#1a6fc4", stroke: "#fff", strokeWidth: 2 }}
              />
              {NODES.map((n) => (
                <ReferenceDot
                  key={n.node}
                  x={n.d}
                  y={n.e}
                  r={6}
                  fill="#5aa72f"
                  stroke="#fff"
                  strokeWidth={2}
                  isFront
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2">
        {NODES.map((n, i) => (
          <motion.div
            key={n.node}
            initial={{ opacity: 0, y: 8 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.9 + i * 0.15, duration: 0.5 }}
            className="flex items-center gap-2"
          >
            <span className="h-2 w-2 rounded-full bg-[#5aa72f]" />
            <span className="font-mono text-xs text-slate-600">{n.node}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
